/**
 * Compliance Issues Log: the Closed issues tab.
 * The record of every issue Compliance has closed: refine it, search it, read
 * the full record, download the list, or reopen an issue with a reason.
 */
import React, { useEffect, useMemo, useState } from 'react';
import HubCorrespondence from '../Calendar/HubCorrespondence';
import {
  cmsDate, daysOpen, closedYear, median, pertainsTo, sourceLabel,
  closedIssueRows, csvText
} from './cilLabels';

const API = process.env.REACT_APP_API_URL || '';
const CIL = `${API}/api/compliance-issues-log`;

const SORTS = [
  ['closed_desc', 'Most recently closed'],
  ['closed_asc', 'Closed longest ago'],
  ['days_desc', 'Longest open'],
  ['days_asc', 'Shortest open'],
  ['identified_desc', 'Most recently identified'],
  ['area', 'Program area A to Z'],
  ['department', 'Department A to Z']
];

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${active ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
      {children}
    </button>
  );
}

function ChipGroup({ label, value, onChange, options }) {
  if (!options.length) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        <Chip active={value === ''} onClick={() => onChange('')}>All</Chip>
        {options.map(([v, l]) => (
          <Chip key={v} active={value === v} onClick={() => onChange(v)}>{l}</Chip>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words">{children === '' || children === null || children === undefined ? <span className="text-slate-400">—</span> : children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function fmtDateTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function downloadCsv(filename, rows) {
  const blob = new Blob(['﻿' + csvText(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function ClosedIssues({ issues, loading, full, canReopen, onReopened }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('closed_desc');
  const [year, setYear] = useState('');
  const [dept, setDept] = useState('');
  const [kind, setKind] = useState('');
  const [impact, setImpact] = useState('');
  const [disclosed, setDisclosed] = useState('');
  const [source, setSource] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [record, setRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [reopenFor, setReopenFor] = useState(null);
  const [reopenNote, setReopenNote] = useState('');
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const yearOptions = useMemo(() => Array.from(new Set(issues.map(closedYear).filter(Boolean)))
    .sort((a, b) => b - a).map((y) => [String(y), String(y)]), [issues]);
  const deptOptions = useMemo(() => {
    const seen = new Map();
    issues.forEach((i) => {
      const ids = Array.isArray(i.department_ids) ? i.department_ids : [];
      const names = String(i.department_names || '').split(',').map((s) => s.trim());
      ids.forEach((id, idx) => { if (id && !seen.has(id)) seen.set(id, names[idx] || 'Department'); });
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [issues]);
  const sourceOptions = useMemo(() => Array.from(new Set(issues.map((i) => i.source).filter(Boolean)))
    .map((s) => [s, sourceLabel(s)]).sort((a, b) => a[1].localeCompare(b[1])), [issues]);

  const anyFilter = Boolean(q || year || dept || kind || impact || disclosed || source);
  const clearFilters = () => { setQ(''); setYear(''); setDept(''); setKind(''); setImpact(''); setDisclosed(''); setSource(''); };

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = issues.filter((i) => {
      if (year && String(closedYear(i)) !== year) return false;
      if (dept && !(Array.isArray(i.department_ids) && i.department_ids.includes(dept))) return false;
      if (kind && pertainsTo(i) !== kind) return false;
      if (impact && (i.member_impact || '') !== impact) return false;
      if (disclosed === 'Y' && !i.date_issue_disclosed_to_cms) return false;
      if (disclosed === 'N' && i.date_issue_disclosed_to_cms) return false;
      if (source && i.source !== source) return false;
      if (needle) {
        const hay = [i.id, i.issue_number, i.program_area_impacted, i.issue_description, i.root_cause_analysis,
          i.department_names, i.accountable_owner_names, i.fdr_entity_name, i.closed_by_name, i.system_remediation_description]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    const t = (v) => (v ? new Date(v).getTime() : 0);
    const s = (v) => String(v || '').toLowerCase();
    const cmp = {
      closed_desc: (a, b) => t(b.closed_at) - t(a.closed_at),
      closed_asc: (a, b) => t(a.closed_at) - t(b.closed_at),
      days_desc: (a, b) => (daysOpen(b) ?? -1) - (daysOpen(a) ?? -1),
      days_asc: (a, b) => (daysOpen(a) ?? 1e9) - (daysOpen(b) ?? 1e9),
      identified_desc: (a, b) => t(b.date_issue_identified) - t(a.date_issue_identified),
      area: (a, b) => s(a.program_area_impacted).localeCompare(s(b.program_area_impacted)),
      department: (a, b) => s(a.department_names).localeCompare(s(b.department_names))
    }[sort] || ((a, b) => t(b.closed_at) - t(a.closed_at));
    return list.slice().sort(cmp);
  }, [issues, q, year, dept, kind, impact, disclosed, source, sort]);

  const thisYear = new Date().getFullYear();
  const stats = useMemo(() => ({
    total: shown.length,
    thisYear: shown.filter((i) => closedYear(i) === thisYear).length,
    days: median(shown.map(daysOpen).filter((d) => d !== null)),
    impact: shown.filter((i) => i.member_impact === 'Y').length
  }), [shown, thisYear]);

  useEffect(() => {
    if (!selectedId) { setRecord(null); return; }
    setRecordLoading(true);
    setError('');
    fetch(`${CIL}/${selectedId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.issue) setRecord(d.issue);
        else setError(d.message || 'The issue could not be opened.');
        setRecordLoading(false);
      })
      .catch(() => { setError('The issue could not be opened.'); setRecordLoading(false); });
  }, [selectedId]);

  const submitReopen = () => {
    if (!reopenFor || !reopenNote.trim()) return;
    setReopening(true);
    setError('');
    fetch(`${CIL}/${reopenFor.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'open', reopen_note: reopenNote.trim() })
    })
      .then((r) => r.json())
      .then((d) => {
        setReopening(false);
        if (d.success) {
          const label = reopenFor.program_area_impacted || `Issue ${String(reopenFor.id).slice(0, 8)}`;
          setReopenFor(null); setReopenNote(''); setSelectedId(null);
          setNotice(`${label} is open again. Find it under Open issues.`);
          if (onReopened) onReopened(reopenFor);
        } else setError(d.message || 'The issue could not be reopened.');
      })
      .catch(() => { setReopening(false); setError('The issue could not be reopened.'); });
  };

  const download = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`closed-issues-${stamp}.csv`, closedIssueRows(shown, { full }));
  };

  const rowFor = (id) => issues.find((i) => i.id === id);
  const selected = selectedId ? (record || rowFor(selectedId)) : null;

  return (
    <div className="space-y-4">
      {notice && (
        <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">Dismiss</button>
        </div>
      )}
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {selected && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">Closed</span>
                <span className="text-xs text-slate-500 font-mono">{String(selected.id).slice(0, 8)}</span>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mt-1">{selected.program_area_impacted || 'Issue'}</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Closed {cmsDate(selected.closed_at) || '—'}{selected.closed_by_name ? ` by ${selected.closed_by_name}` : ''}
                {daysOpen(selected) !== null ? ` · open ${daysOpen(selected)} day${daysOpen(selected) === 1 ? '' : 's'}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canReopen && (
                <button type="button" onClick={() => { setReopenFor(selected); setReopenNote(''); }}
                  className="px-4 py-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100">
                  Reopen issue
                </button>
              )}
              <button type="button" onClick={() => setSelectedId(null)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">
                Back to closed issues
              </button>
            </div>
          </div>

          {recordLoading && !record ? (
            <p className="text-sm text-slate-500">Opening the record…</p>
          ) : (
            <div className="space-y-4">
              <Section title="The issue">
                <Field label="Pertains to">{pertainsTo(selected) === 'FDR' ? `FDR${selected.fdr_entity_name ? ': ' + selected.fdr_entity_name : ''}` : 'Internal health plan'}</Field>
                <Field label="Source">{sourceLabel(selected.source)}</Field>
                <Field label="Department">{selected.department_names || selected.department_name}</Field>
                <Field label="Accountable owner">{selected.accountable_owner_names || selected.accountable_owner_name}</Field>
                <Field label="Date identified">{cmsDate(selected.date_issue_identified)}</Field>
                <Field label="Member impact">{selected.member_impact === 'Y' ? `Yes (${selected.enrollees_impacted ?? '—'} enrollees)` : selected.member_impact === 'N' ? 'No' : ''}</Field>
                <Field label="Issue description" wide>{selected.issue_description}</Field>
                <Field label="Root cause analysis" wide>{selected.root_cause_analysis}</Field>
              </Section>
              {full && (
                <Section title="Disclosure to CMS">
                  <Field label="Date disclosed to CMS">{cmsDate(selected.date_issue_disclosed_to_cms)}</Field>
                  <Field label="Disclosed to">{selected.disclosed_to_name}</Field>
                  <Field label="Fully remediated">{selected.fully_remediated_flag === 'Y' ? 'Yes' : selected.fully_remediated_flag === 'N' ? 'No' : ''}</Field>
                </Section>
              )}
              <Section title="Remediation">
                <Field label="System or operational remediation" wide>{selected.system_remediation_description}</Field>
                <Field label="Remediation started">{cmsDate(selected.system_remediation_started_at)}</Field>
                <Field label="Remediation completed">{cmsDate(selected.system_remediation_completed_at)}</Field>
              </Section>
              <Section title="Closure record">
                <Field label="Closed on">{cmsDate(selected.closed_at)}</Field>
                <Field label="Closed by">{selected.closed_by_name || (selected.closed_at ? 'Not recorded' : '')}</Field>
                {selected.reopened_at && (
                  <>
                    <Field label="Last reopened">{`${cmsDate(selected.reopened_at)}${selected.reopened_by_name ? ' by ' + selected.reopened_by_name : ''}`}</Field>
                    <Field label="Reason it was reopened">{selected.reopen_note}</Field>
                  </>
                )}
              </Section>
              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Owner updates</h3>
                {!record ? (
                  <p className="text-sm text-slate-500">Opening the record…</p>
                ) : (record.owner_updates || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No owner updates were recorded on this issue.</p>
                ) : (
                  <div className="space-y-2">
                    {record.owner_updates.map((u) => (
                      <div key={u.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">{fmtDateTime(u.created_at)} · {u.submitted_by_name || 'Owner'}</p>
                        <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{u.update_text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {full && (
                <details className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                  <summary className="text-sm font-semibold text-slate-700 cursor-pointer select-none">Correspondence on this issue</summary>
                  <div className="mt-3">
                    <HubCorrespondence base="/api/compliance-issues-log/correspondence" item={selected.id} chipFor={() => 'Issue'}
                      intro="Every email sent from Workstream about this issue, with its follow-ups and replies." />
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {reopenFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !reopening && setReopenFor(null)}>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">Reopen this issue</h2>
            <p className="text-sm text-slate-500 mt-1">{reopenFor.program_area_impacted || `Issue ${String(reopenFor.id).slice(0, 8)}`}. It returns to Open issues and the reason stays on its record.</p>
            <label className="block text-sm font-medium text-slate-700 mt-4 mb-1">Why is it being reopened?</label>
            <textarea value={reopenNote} onChange={(e) => setReopenNote(e.target.value)} rows={3} autoFocus
              placeholder="e.g. Monitoring found the same gap in the July sample."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" disabled={reopening} onClick={() => setReopenFor(null)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">Keep closed</button>
              <button type="button" disabled={reopening || !reopenNote.trim()} onClick={submitReopen} className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                {reopening ? 'Reopening…' : 'Reopen issue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Refine</h3>
                <p className="text-xs text-slate-500 mt-0.5">Narrow the closed issues</p>
              </div>
              {anyFilter && <button type="button" onClick={clearFilters} className="text-xs font-semibold text-blue-600 hover:underline">Clear</button>}
            </div>
            <div className="px-5 py-4 space-y-5">
              <ChipGroup label="Year closed" value={year} onChange={setYear} options={yearOptions} />
              <ChipGroup label="Department" value={dept} onChange={setDept} options={deptOptions} />
              <ChipGroup label="Pertains to" value={kind} onChange={setKind} options={[['Internal', 'Internal'], ['FDR', 'FDR']]} />
              <ChipGroup label="Member impact" value={impact} onChange={setImpact} options={[['Y', 'Yes'], ['N', 'No']]} />
              {full && <ChipGroup label="Disclosed to CMS" value={disclosed} onChange={setDisclosed} options={[['Y', 'Yes'], ['N', 'No']]} />}
              <ChipGroup label="Source" value={source} onChange={setSource} options={sourceOptions} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Closed', value: stats.total },
              { label: `Closed in ${thisYear}`, value: stats.thisYear },
              { label: 'Typical days open', value: stats.days === null ? '—' : stats.days },
              { label: 'With member impact', value: stats.impact }
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3">
                <div className="text-xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-9">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
              <div className="mr-auto">
                <h3 className="text-sm font-semibold text-slate-900">Closed issues</h3>
                <p className="text-xs text-slate-500 mt-0.5">{shown.length !== issues.length ? `${shown.length} of ${issues.length} shown` : `${issues.length} total`}</p>
              </div>
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search closed issues"
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white w-56" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button type="button" onClick={download} disabled={!shown.length}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
                Download list
              </button>
            </div>
            {loading ? (
              <div className="py-12 text-center text-slate-500">Loading…</div>
            ) : issues.length === 0 ? (
              <div className="py-14 text-center">
                <p className="text-slate-700 font-medium">No closed issues yet</p>
                <p className="text-slate-500 text-sm mt-1">When Compliance closes an issue it moves here with its full record.</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="py-12 text-center text-slate-500">No closed issues match. <button type="button" onClick={clearFilters} className="text-blue-600 font-medium hover:underline">Clear the filters</button></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-700 font-medium">
                      <th className="py-3 px-3">#</th>
                      <th className="py-3 px-3">Program area</th>
                      <th className="py-3 px-3">Department</th>
                      <th className="py-3 px-3">Identified</th>
                      <th className="py-3 px-3">Closed</th>
                      <th className="py-3 px-3">Days open</th>
                      <th className="py-3 px-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shown.map((row) => {
                      const d = daysOpen(row);
                      const tags = [];
                      if (pertainsTo(row) === 'FDR') tags.push(`FDR${row.fdr_entity_name ? ': ' + row.fdr_entity_name : ''}`);
                      if (row.member_impact === 'Y') tags.push(`Member impact${row.enrollees_impacted ? ` (${row.enrollees_impacted})` : ''}`);
                      if (full && row.date_issue_disclosed_to_cms) tags.push('Disclosed to CMS');
                      return (
                        <tr key={row.id} className={`hover:bg-slate-50 ${selectedId === row.id ? 'bg-blue-50/40' : ''}`}>
                          <td className="py-3 px-3 text-slate-600 font-mono text-xs">{row.issue_number || String(row.id).slice(0, 8)}</td>
                          <td className="py-3 px-3 text-slate-800">
                            <button type="button" onClick={() => setSelectedId(row.id)} className="text-left font-medium hover:underline">{row.program_area_impacted || 'Issue'}</button>
                            {row.issue_description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1 max-w-sm">{row.issue_description}</div>}
                            {tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {tags.map((t) => <span key={t} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">{t}</span>)}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3 text-slate-600">{row.department_names || row.department_name || '—'}</td>
                          <td className="py-3 px-3 text-slate-600 whitespace-nowrap">{cmsDate(row.date_issue_identified) || '—'}</td>
                          <td className="py-3 px-3 text-slate-600 whitespace-nowrap">
                            {cmsDate(row.closed_at) || '—'}
                            <div className="text-xs text-slate-400">{row.closed_by_name ? `by ${row.closed_by_name}` : 'closer not recorded'}</div>
                          </td>
                          <td className="py-3 px-3 text-slate-600">{d === null ? '—' : d}</td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className="flex items-center gap-3">
                              <button type="button" onClick={() => setSelectedId(row.id)} className="text-blue-600 hover:underline font-medium">View</button>
                              {canReopen && <button type="button" onClick={() => { setReopenFor(row); setReopenNote(''); }} className="text-amber-600 hover:underline font-medium">Reopen</button>}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
